import { useCallback, useEffect, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";

const SPRING = { stiffness: 38, damping: 30, mass: 0.95 };

/**
 * Cursor-driven hero ambience: soft dual-radial wash + optional column tilt.
 * Disabled without fine pointer or when prefers-reduced-motion is set.
 */
export function useHeroAmbience() {
  const reduceMotion = useReducedMotion();
  const [finePointer, setFinePointer] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(hover: hover) and (pointer: fine)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const onChange = () => setFinePointer(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const nx = useMotionValue(0.52);
  const ny = useMotionValue(0.34);
  const sx = useSpring(nx, SPRING);
  const sy = useSpring(ny, SPRING);

  const active = Boolean(!reduceMotion && finePointer);

  const onMouseMove = useCallback(
    (e) => {
      if (!active) return;
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const w = Math.max(r.width, 1);
      const h = Math.max(r.height, 1);
      nx.set((e.clientX - r.left) / w);
      ny.set((e.clientY - r.top) / h);
    },
    [active, nx, ny]
  );

  const onMouseLeave = useCallback(() => {
    nx.set(0.52);
    ny.set(0.34);
  }, [nx, ny]);

  const spotlightBg = useTransform([sx, sy], ([x, y]) => {
    const xp = (typeof x === "number" ? x : 0.52) * 100;
    const yp = (typeof y === "number" ? y : 0.34) * 100;
    return `radial-gradient(620px circle at ${xp}% ${yp}%, rgba(231,109,56,0.1), transparent 56%), radial-gradient(460px circle at ${100 - xp * 0.82}% ${100 - yp * 0.72}%, rgba(134,184,204,0.075), transparent 52%)`;
  });

  const leftRotateX = useTransform(sy, (y) => {
    if (!active) return 0;
    const yn = typeof y === "number" ? y : 0.34;
    return (0.42 - yn) * 5.5;
  });

  const leftRotateY = useTransform(sx, (x) => {
    if (!active) return 0;
    const xn = typeof x === "number" ? x : 0.52;
    return (xn - 0.48) * -6.5;
  });

  const rightRotateX = useTransform(sy, (y) => {
    if (!active) return 0;
    const yn = typeof y === "number" ? y : 0.34;
    return (yn - 0.5) * 3.2;
  });

  const rightRotateY = useTransform(sx, (x) => {
    if (!active) return 0;
    const xn = typeof x === "number" ? x : 0.52;
    return (xn - 0.52) * 4.2;
  });

  return {
    handlers: { onMouseMove, onMouseLeave },
    spotlightBg,
    tiltLeft: { rotateX: leftRotateX, rotateY: leftRotateY },
    tiltRight: { rotateX: rightRotateX, rotateY: rightRotateY },
  };
}

/** Soft light field that follows the pointer (no images). */
export function HeroCursorGlow({ backgroundImage }) {
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-[1]"
      style={{ backgroundImage }}
    />
  );
}
